// @flow

// The ObscuredOverlay is shown over PinVisual when the user hides a pin. It obscures the pin
// and displays some markup for undoing the hide, if applicable.

import React, { useEffect, useState } from 'react';
import ContentLayer from 'app/common/react/components/PinRep/components/ContentLayer';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import { callPinFeedbackAction } from 'app/packages/pin-utils/feedbackActions';
import { PIN_BORDER_RADIUS } from 'app/common/react/components/PinRep/core/PinRepPresentation';
import { track } from 'app/common/lib/react/analytics';
import { type FeedbackType } from 'app/common/react/components/PinRep/feedback-types';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { Box, Button, Image, Mask, Text } from 'gestalt';

const styles = {
  background: {
    base: {
      transition: 'all 250ms',
      opacity: 0,
    },
    blurred: {
      backgroundPosition: '50% 50%',
      backgroundSize: 'cover',
      borderRadius: PIN_BORDER_RADIUS,
      filter: 'blur(20px)',
      opacity: 1,
    },
  },
  backdrop: {
    base: {
      transition: 'all 250ms',
    },
    blurred: {
      backgroundColor: 'rgba(0,0,0,0.4)',
    },
  },
  feedbackOverlayContainer: {
    base: {
      opacity: 0,
      transition: 'all 250ms',
    },
    visible: {
      opacity: 1,
    },
  },
  navigateLinkStyle: {
    color: 'white',
    textDecorationColor: 'white',
    cursor: 'pointer',
  },
};

export const noHomefeedControlFeedbackTypes = ['followed', 'related', 'search'];
export const pfyFeedbackTypes = ['pfy', 'pfyBoard'];

// For use in PinRepContainer
export type DerivedProps = {|
  feedbackType: ?FeedbackType,
  isHomefeedControlPanelExp: boolean,
|};

type OwnProps = {|
  pin: Pin,
  ...DerivedProps,
|};

export type Props = {|
  ...OwnProps,
|};

export default function ObscuredOverlay({ feedbackType, isHomefeedControlPanelExp, pin }: Props) {
  const [isBlurred, setBlurState] = useState(false);
  const [isUnfollowed, setUnfollowState] = useState(false);

  // Change style state after mounting to trigger css animation
  useEffect(() => setBlurState(true), []);

  const unfollowCallback = () => setUnfollowState(true);

  const { feedbackText, images: pinImages, showFeedbackOverlay, undoAction, undoText } = pin;

  const pinImageUrl = pinImages && pinImages['236x'].url;

  const { image, subTitle, title, unfollow } = feedbackText;

  const showHomefeedControl =
    isHomefeedControlPanelExp && !noHomefeedControlFeedbackTypes.includes(feedbackType);

  // We shouldn't show Unfollow button if pin is PFY ("Picked For You") as it's ambiguous what user is unfollowing
  const isPfy = pfyFeedbackTypes.includes(feedbackType);

  const logHomefeedControl = () => {
    track(PCONST.thrift.EventType.CLICK, {
      view: PCONST.thrift.ViewType.FEED,
      viewParameter: PCONST.thrift.ViewParameterType.FEED_HOME,
      element: PCONST.thrift.ElementType.HOME_FEED_CONTROL_PANEL_PIN_FEEDBACK_BUTTON,
    });
  };

  return (
    <Box
      color="transparent"
      dangerouslySetInlineStyle={{
        __style: {
          borderRadius: PIN_BORDER_RADIUS,
        },
      }}
      data-test-id="obscured-overlay"
      overflow="hidden"
      position="absolute"
      top
      left
      bottom
      right
    >
      <Box
        dangerouslySetInlineStyle={{
          __style: { borderRadius: PIN_BORDER_RADIUS },
        }}
        margin={-4}
        overflow="hidden"
        position="absolute"
        top
        left
        bottom
        right
      >
        {/* Double the blur, double the fun! */}
        {pinImageUrl && (
          <ContentLayer
            additionalStyles={{
              ...styles.background.base,
              ...(isBlurred ? styles.background.blurred : {}),
              backgroundImage: `url("${pinImageUrl}")`,
            }}
          />
        )}
        {pinImageUrl && (
          <ContentLayer
            additionalStyles={{
              ...styles.background.base,
              ...(isBlurred ? styles.background.blurred : {}),
              backgroundImage: `url("${pinImageUrl}")`,
            }}
          />
        )}
        <ContentLayer
          additionalStyles={{
            ...styles.backdrop.base,
            ...(isBlurred ? styles.backdrop.blurred : {}),
          }}
        />
      </Box>
      <Box
        dangerouslySetInlineStyle={{
          __style: {
            ...styles.feedbackOverlayContainer.base,
            ...(!showFeedbackOverlay && isBlurred ? styles.feedbackOverlayContainer.visible : {}),
          },
        }}
        height="100%"
        padding={4}
        position="relative"
        width="100%"
      >
        <Box display="flex" direction="column" height="100%" justifyContent="between">
          <Box className="ObscuredOverlay" data-test-id="PinFeedbackConfirmation" maxWidth={216}>
            <Box marginBottom={2}>
              <Text color="white" size="xl" weight="bold">
                {title}
              </Text>
            </Box>

            <Box display="flex">
              {image && (
                <Box marginRight={3}>
                  <Mask width={image.width} height={image.height} shape="rounded" wash>
                    <Image
                      alt={subTitle || ''}
                      fit="cover"
                      naturalHeight={1}
                      naturalWidth={1}
                      src={image.url || ''}
                    />
                  </Mask>
                </Box>
              )}
              <Text color="white" overflow="normal">
                {subTitle}
                {showHomefeedControl && (
                  <NavigateLink
                    className="underlineLink"
                    to="/edit"
                    onClick={logHomefeedControl}
                    style={styles.navigateLinkStyle}
                  >
                    {i18n._(
                      ' Tune your home feed',
                      'Text on the button to navigate to homefeed control',
                    )}
                  </NavigateLink>
                )}
              </Text>
            </Box>
          </Box>

          {/*undoAction will exist if/only if user is in the undoHide experiment and hid a pin*/}
          {!undoAction &&
            !isUnfollowed &&
            unfollow &&
            !isPfy && (
              <Button
                color="white"
                onClick={() =>
                  callPinFeedbackAction(unfollow.action, unfollow.actionOptions, unfollowCallback)
                }
                text={i18n._('Unfollow', 'Text on the button to unfollow a specific board / user')}
              />
            )}

          {undoAction && (
            <Button
              color="white"
              onClick={undoAction}
              text={undoText || i18n._('Undo', 'Text on the button to unfollow hiding a pin')}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
